import Component from "../../lib/component";
import {
    getCarouselSlides,
    getCarouselPagination,
    getCarouselControls
} from "./helpers/component_creators";
import {getDebouncedFunction} from "../../helpers/debounce";
import {observeWhenLaidOut, entryHasArea} from "../../helpers/laid_out_intersection_observer";
import {SlideRandomizer} from "./lib/slide_randomizer";
import {SlideNumberer} from "./lib/slide_numberer";

/**
 * Carousel Component
 * 
 * A flexible carousel component that supports both existing HTML content and configuration-based slides.
 * 
 * Features:
 * - Automatically wraps existing child elements as slides
 * - Supports configuration-based slide creation
 * - Smooth scroll-snap navigation
 * - Keyboard navigation (arrow keys)
 * - Touch/swipe support
 * - Autoplay functionality with play/pause controls
 * - Pagination dots
 * - Previous/Next navigation buttons
 * - Responsive and accessible
 * 
 * Usage:
 * 1. With existing content:
 *    <div class="carousel">
 *      <img src="slide1.jpg" alt="Slide 1">
 *      <div>Slide 2 content</div>
 *    </div>
 * 
 * 2. With configuration:
 *    <div class="carousel" data-carousel__config='{"slides": [...]}'>
 *    </div>
 * 
 * 3. Mixed (existing content + config):
 *    <div class="carousel" data-carousel__config='{"slides": [...]}'>
 *      <img src="existing-slide.jpg" alt="Existing">
 *    </div>
 * 
 * The component will:
 * - First wrap any existing child elements in slide containers
 * - Then add any slides defined in the configuration
 * - Initialize navigation controls and autoplay if configured
 */
/**
 * Configuration object for the Carousel component
 * 
 * Dataset configuration keys:
 * - slides: Array of slide configurations
 * - play_on_view: Boolean to start autoplay on load
 * - autoplay_interval: Number of milliseconds between slides
 * - loop: Boolean to enable infinite loop
 * 
 * Example dataset configuration:
 * data-carousel__config='{
 *   "slides": [
 *     {"type": "image", "src": "slide1.jpg", "alt": "Slide 1"},
 *     {"type": "html", "content": "<div>Custom HTML</div>"}
 *   ],
 *   "play_on_view": true,
 *   "autoplay_interval": 5000,
 *   "loop": true
 * }'
 */
export const CarouselConfig = {
    componentTypeKey: 'Carousel',
    baseHtmlClass: 'carousel',
    selector: '.carousel',

    datasetConfigKey: 'carousel__config',
    slidesDatasetConfigKey: 'slides',
    playOnLoadDatasetConfigKey: 'play_on_view',
    autoplayIntervalDatasetConfigKey: 'autoplay_interval',
    loopDatasetConfigKey: 'loop',
    zoomableDatasetConfigKey: 'zoomable',
    numberedDatasetConfigKey: 'numbered',
    randomizeDatasetConfigKey: 'randomize',

    viewportSelector: '.carousel__viewport',
    slideSelector: '.carousel__slide',
    paginationSelector: '.carousel__pagination',
    controlsSelector: '.carousel__controls',

    activeClassModifier: 'active',
    loadingClassModifier: 'loading',
    loadedClassModifier: 'loaded',
    errorClassModifier: 'error',
    playingClassModifier: 'playing',

    defaultAutoplayInterval: 3000,
    defaultLoop: true,
    defaultZoomable: true,
    
    maxProximityDistance: 2,
    prevClassPrefix: 'prev',
    nextClassPrefix: 'next',
    currentClassModifier: 'current',
    forwardClassModifier: 'forward',
    backwardClassModifier: 'backward',
    farPrevClassModifier: 'far-prev',
    farNextClassModifier: 'far-next',
};

export class Carousel extends Component {

    static get config() {
        return CarouselConfig;
    }

    /**
     * Creates a new Carousel instance
     * @param {string|HTMLElement} id_or_element - The carousel element or its ID
     */
    constructor(id_or_element) {
        super({
            element: id_or_element,
            config: CarouselConfig
        });

        this.setDatasetConfig(CarouselConfig.datasetConfigKey);

        console.log("[Carousel] Initializing Carousel with config:", this.datasetConfig);
        
        this.currentSlideIndex = 0;
        this.isAutoPlaying = false;
        this.autoplayInterval = null;
        this.intersectionObserver = null;
        this.viewportObserver = null;
        this.resizeObserver = null;
        // ResizeObservers created by the shared laid-out-observe helper (one per deferred observe),
        // held so destroy() can release any still waiting for their target to gain area.
        this.laidOutResizeObservers = [];
        this.hasAutoplayStarted = false;
        this.isProgrammaticScroll = false;
        this.isAutoplayScrolling = false;
        this.carouselInteractionActive = false;
        this.lastViewportScrollLeft = 0;
        this.isNumbered = this.getDatasetConfigValue(CarouselConfig.numberedDatasetConfigKey) === true;
        this.isRandomized = this.getDatasetConfigValue(CarouselConfig.randomizeDatasetConfigKey) === true;

        // Capture existing content before creating structure
        this.existingContent = this.getExistingContentElements();

        this.createStructure();
        this.setupScrollSnap();
        
        // Create slides from existing content and config
        this.createSlidesFromConfig();
        
        // Randomize slides if enabled (must happen before numbering)
        if (this.isRandomized) {
            this.randomizeSlides();
        }
        
        // Add slide numbers if enabled
        if (this.isNumbered) {
            this.addSlideNumbers();
        }
        
        // Then initialize components that depend on slides
        this.slides = getCarouselSlides(this);
        this.pagination = getCarouselPagination(this);
        this.controls = getCarouselControls(this);
        
        // Add pagination numbers if enabled (after pagination is created)
        if (this.isNumbered && this.pagination) {
            this.addPaginationNumbers();
        }

        this.setupIntersectionObserver();
        this.setupUserInteractionListeners();
        this.setupScrollListener();
        this.setupResizeObserver();
        this.setupCarouselViewportObserver();

        // Register teardowns so the base destroy() (run by the registry reaper when this element
        // leaves the DOM) releases everything that outlives the element and deregisters the
        // component. Each reads its field at teardown time — the observers are created in the
        // setup methods above, and this.slides is rebuilt on relayout (createSlidesFromConfig).
        this.onDestroy(() => { if (this.autoplayInterval) clearInterval(this.autoplayInterval); });
        this.onDestroy(() => this.intersectionObserver?.disconnect());
        this.onDestroy(() => this.viewportObserver?.disconnect());
        this.onDestroy(() => this.resizeObserver?.disconnect());
        this.onDestroy(() => this.laidOutResizeObservers.forEach(observer => observer.disconnect()));
        this.onDestroy(() => this.slides?.forEach(slide => slide.destroy?.()));

        this.updateActiveStates();
        
        // Render all slides after proximity states are set
        this.slides.forEach(slide => slide.render());
        
        this.render();
    }

    /**
     * Creates the carousel's DOM structure if it doesn't exist
     * Ensures viewport, pagination, and controls elements are present
     */
    createStructure() {
        // Create viewport if it doesn't exist
        this.viewport = this.element.querySelector(CarouselConfig.viewportSelector);
        if (!this.viewport) {
            this.viewport = document.createElement('div');
            this.viewport.className = 'carousel__viewport';
            this.element.appendChild(this.viewport);
        }

        // Create pagination if it doesn't exist
        let pagination = this.element.querySelector(CarouselConfig.paginationSelector);
        if (!pagination) {
            pagination = document.createElement('div');
            pagination.className = 'carousel__pagination';
            this.element.appendChild(pagination);
        }

        // Create controls if it doesn't exist
        let controls = this.element.querySelector(CarouselConfig.controlsSelector);
        if (!controls) {
            controls = document.createElement('div');
            controls.className = 'carousel__controls';
            
            const prevBtn = document.createElement('button');
            prevBtn.className = 'carousel__control carousel__control--prev';
            prevBtn.textContent = 'Prev';
            prevBtn.setAttribute('type', 'button');
            
            const nextBtn = document.createElement('button');
            nextBtn.className = 'carousel__control carousel__control--next';
            nextBtn.textContent = 'Next';
            nextBtn.setAttribute('type', 'button');
            
            const toggleBtn = document.createElement('button');
            toggleBtn.className = 'carousel__control carousel__control--toggle';
            toggleBtn.textContent = 'Play';
            toggleBtn.setAttribute('type', 'button');
            toggleBtn.setAttribute('aria-pressed', 'false');
            toggleBtn.setAttribute('aria-label', 'Play slideshow');
            
            controls.appendChild(prevBtn);
            controls.appendChild(nextBtn);
            controls.appendChild(toggleBtn);
            this.element.appendChild(controls);
        }
    }

    setupScrollSnap() {
        if (!this.viewport) return;
        
        Object.assign(this.viewport.style, {
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            display: 'flex'
        });
    }

    /**
     * Creates carousel slides from both existing content and configuration
     * 
     * Process:
     * 1. First wraps any existing child elements in slide containers
     * 2. Then creates additional slides from configuration data
     * 3. Ensures proper active state management
     * 
     * This allows the carousel to work with:
     * - Pre-existing HTML content (progressive enhancement)
     * - Configuration-based dynamic content
     * - A combination of both
     */
    createSlidesFromConfig() {
        let slideIndex = 0;

        // First, wrap and move existing elements into slides
        if (this.existingContent && this.existingContent.length > 0) {
            console.log("[Carousel] Found existing content elements:", this.existingContent.length);
            this.existingContent.forEach((child) => {
                const slideElement = document.createElement('div');
                slideElement.className = 'carousel__slide';
                if (slideIndex === 0) slideElement.classList.add('carousel__slide--active');
                
                // Move the existing element into the slide wrapper
                slideElement.appendChild(child);
                this.viewport.appendChild(slideElement);
                slideIndex++;
            });
            // Clear the reference after moving elements
            this.existingContent = null;
        }

        // Then add slides from config
        const configSlides = this.getDatasetConfigValue(CarouselConfig.slidesDatasetConfigKey) || [];
        console.log("[Carousel] Config Slides:", configSlides);

        configSlides.forEach((slideConfig, index) => {
            const slideElement = document.createElement('div');
            slideElement.className = 'carousel__slide';
            // Only mark as active if there were no existing slides
            if (slideIndex === 0 && index === 0) slideElement.classList.add('carousel__slide--active');
            this.viewport.appendChild(slideElement);
        });

        // Re-initialize slides array after creating elements
        this.slides = getCarouselSlides(this);
        
        // Store config for slides to access during initialization
        this.slideConfigs = configSlides;
    }

    /**
     * Gets existing child elements that should become carousel slides
     * 
     * Filters out structural elements (viewport, pagination, controls) to find
     * only content elements that should be wrapped as slides
     * 
     * @returns {Array<HTMLElement>} Array of child elements to convert to slides
     */
    getExistingContentElements() {
        // Get all direct children of the carousel element that aren't part of the carousel structure
        const structuralSelectors = [
            CarouselConfig.viewportSelector,
            CarouselConfig.paginationSelector,
            CarouselConfig.controlsSelector
        ];
        
        const children = Array.from(this.element.children);
        return children.filter(child => {
            // Check if this child matches any of the structural selectors
            return !structuralSelectors.some(selector => child.matches(selector));
        });
    }

    setupIntersectionObserver() {
        if (!this.viewport) return;

        this.intersectionObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                // ⚠️ A ZERO-AREA TARGET REPORTS isIntersecting WITH A RATIO OF 1.
                //
                // The ratio is not an area quotient, so a slide with no box satisfies any
                // threshold — and when the slides have no box they ALL do, at once. This
                // forEach would then call setCurrentSlide for each in turn and the LAST one
                // would win, leaving the carousel showing slide 1 with the final dot lit and
                // nothing correcting it, because nothing further intersects. A carousel built
                // before its stylesheet applies is exactly that shape.
                //
                // Guard on degeneracy, never on smallness: a carousel built mid-animation at
                // 83px is correct, because slide 0 is slide 0 at every width. A "too small"
                // threshold would reject that innocent case and keep the guilty one.
                //
                // Same trap as .claude/rules/native-webview-unmeasured-load.md. The degeneracy
                // test now lives in the shared helper; behaviour is unchanged.
                if (!entryHasArea(entry)) return;

                if (entry.isIntersecting) {
                    const slideIndex = this.slides.findIndex(slide => slide.element === entry.target);
                    if (slideIndex !== -1 && slideIndex !== this.currentSlideIndex) {
                        // Only update if not during programmatic scroll to avoid conflicts
                        if (!this.isProgrammaticScroll) {
                            this.setCurrentSlide(slideIndex);
                        }
                    }
                }
            });
        }, {
            root: this.viewport,
            threshold: 0.5
        });

        // Defer each slide's observe until it has real area, so the first delivery cannot be the
        // zero-area lie (a slide built before its stylesheet applies has zero width at a settled
        // viewport). The fire-time refusal above still covers a later collapse.
        this.slides.forEach(slide => {
            const laidOut = observeWhenLaidOut(this.intersectionObserver, slide.element);
            if (laidOut) this.laidOutResizeObservers.push(laidOut);
        });
    }

    setupUserInteractionListeners() {
        if (!this.viewport) return;
        
        // Track when user starts interacting with carousel
        this.viewport.addEventListener('mousedown', () => {
            this.carouselInteractionActive = true;
            console.log('[Carousel] Mouse down on carousel');
        });
        
        this.viewport.addEventListener('touchstart', () => {
            this.carouselInteractionActive = true;
            console.log('[Carousel] Touch start on carousel');
        }, { passive: true });
        
        // Clear flag when interaction ends
        this.viewport.addEventListener('mouseup', () => {
            setTimeout(() => {
                this.carouselInteractionActive = false;
                console.log('[Carousel] Mouse interaction ended');
            }, 100);
        });
        
        this.viewport.addEventListener('touchend', () => {
            setTimeout(() => {
                this.carouselInteractionActive = false;
                console.log('[Carousel] Touch interaction ended');
            }, 100);
        }, { passive: true });
        
        // Direct wheel event on carousel viewport
        this.viewport.addEventListener('wheel', (e) => {
            if (this.isAutoPlaying) {
                console.log('[Carousel] Wheel event on carousel - stopping autoplay');
                this.stopAutoplay();
            }
        }, { passive: true });
        
        // Keyboard only when carousel or its children have focus
        this.element.addEventListener('keydown', (e) => {
            // Check if carousel or its children have focus
            if (!this.element.contains(document.activeElement)) return;
            
            if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                console.log('[Carousel] Keyboard navigation on focused carousel');
                if (this.isAutoPlaying) {
                    this.stopAutoplay();
                }
                
                if (e.key === 'ArrowLeft') {
                    e.preventDefault();
                    this.previousSlide();
                } else if (e.key === 'ArrowRight') {
                    e.preventDefault();
                    this.nextSlide();
                }
            }
        });
    }

    setupScrollListener() {
        if (!this.viewport) return;
        
        // Store initial scroll position
        this.lastViewportScrollLeft = this.viewport.scrollLeft;
        
        const debouncedStopAutoplay = getDebouncedFunction(() => {
            // Only stop if carousel interaction is active
            if (this.isAutoPlaying && this.carouselInteractionActive) {
                console.log('[Carousel] Stopping autoplay due to carousel interaction');
                this.stopAutoplay();
            }
        }, 50);
        
        this.viewport.addEventListener('scroll', () => {
            const currentScrollLeft = this.viewport.scrollLeft;
            const scrollChanged = Math.abs(currentScrollLeft - this.lastViewportScrollLeft) > 5;
            
            // Only consider it user scroll if:
            // 1. Not programmatic/autoplay scroll
            // 2. Scroll position actually changed
            // 3. Carousel interaction is active (user is interacting with carousel)
            if (!this.isProgrammaticScroll && !this.isAutoplayScrolling && scrollChanged) {
                if (this.carouselInteractionActive && this.isAutoPlaying) {
                    console.log('[Carousel] User scrolling carousel - triggering stop autoplay');
                    debouncedStopAutoplay();
                }
            }
            
            console.log('[Carousel] scroll event - autoplay:', this.isAutoPlaying, 'programmatic:', this.isProgrammaticScroll, 'autoplayScrolling:', this.isAutoplayScrolling, 'interactionActive:', this.carouselInteractionActive, 'scrollChanged:', scrollChanged);
            this.lastViewportScrollLeft = currentScrollLeft;
        });
    }

    // Every piece of positional bookkeeping is computed once at construction, and nothing else
    // revisits it — ImageTransform's own ResizeObserver re-fits the IMAGE, which is why a
    // stale carousel shows a correct picture under a wrong dot.
    //
    // Worth knowing what this is and is not: CSS scroll-snap already re-anchors scrollLeft
    // across a resize, so the dots keep step without this. It earns its place by making the
    // component answerable for a container it does not control — a rotating or split-screening
    // webview, a resizing modal — rather than trusting one measurement for ever.
    setupResizeObserver() {
        if (!this.viewport || typeof ResizeObserver === 'undefined') return;

        // Long enough not to fire per frame through a 500ms width animation, short enough to
        // settle promptly after one.
        const resync = getDebouncedFunction(this.resyncToScrollPosition.bind(this), 100);
        this.resizeObserver = new ResizeObserver(resync);
        this.resizeObserver.observe(this.viewport);
    }

    // The scroll position is what a person can see; the index is derived bookkeeping. When the
    // two disagree the visible one wins — scrolling BACK to a remembered index would take a
    // carousel correctly showing slide 1 and jump it to slide 2, turning a cosmetic defect into
    // a content one, and would yank a person who is mid-swipe during a rotation.
    resyncToScrollPosition() {
        if (!this.viewport || !this.slides || this.slides.length === 0) return;

        // A scroll we started ourselves is already heading somewhere; re-syncing mid-flight
        // would fight it.
        if (this.isProgrammaticScroll || this.isAutoplayScrolling) return;

        const width = this.viewport.clientWidth;
        if (width <= 0) return;

        const index = Math.round(this.viewport.scrollLeft / width);
        const clamped = Math.max(0, Math.min(index, this.slides.length - 1));

        this.lastViewportScrollLeft = this.viewport.scrollLeft;
        if (clamped === this.currentSlideIndex) return;

        this.currentSlideIndex = clamped;
        this.updateActiveStates();
        this.slides.forEach(slide => slide.render());
        this.render();
    }

    setupCarouselViewportObserver() {
        if (!this.shouldPlayOnLoad()) return;

        this.viewportObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && !this.hasAutoplayStarted && !this.isAutoPlaying) {
                    console.log('[Carousel] Entered viewport, starting autoplay');
                    this.hasAutoplayStarted = true;
                    this.startAutoplay();
                }
            });
        }, {
            threshold: 0.1 // Start when 10% of carousel is visible
        });

        const laidOut = observeWhenLaidOut(this.viewportObserver, this.element);
        if (laidOut) this.laidOutResizeObservers.push(laidOut);
    }

    setCurrentSlide(index) {
        if (index < 0 || index >= this.slides.length) return;
        
        console.log('[Carousel] setCurrentSlide - from:', this.currentSlideIndex, 'to:', index, 'autoplay:', this.isAutoPlaying);
        this.currentSlideIndex = index;
        this.updateActiveStates();
        this.render();
    }

    updateActiveStates() {
        const totalSlides = this.slides.length;
        const isLooping = this.shouldLoop();
        
        this.slides.forEach((slide, index) => {
            let signedDistance;
            
            if (isLooping && totalSlides > 1) {
                // Calculate loop-aware distance
                const forwardDistance = (index - this.currentSlideIndex + totalSlides) % totalSlides;
                const backwardDistance = forwardDistance - totalSlides;
                
                // Choose the shortest path
                signedDistance = Math.abs(forwardDistance) <= Math.abs(backwardDistance) 
                    ? forwardDistance 
                    : backwardDistance;
            } else {
                // Simple linear distance for non-looping
                signedDistance = index - this.currentSlideIndex;
            }
            
            // Set proximity state first
            slide.setProximityState(signedDistance);
            
            // Set active state but avoid extra render calls during initialization
            slide.setActiveWithoutRender(index === this.currentSlideIndex);
        });

        this.pagination?.updateActiveDot(this.currentSlideIndex);
    }

    goToSlide(index, triggeredByAutoplay = false) {
        if (index < 0 || index >= this.slides.length) return;
        if (!this.slides[index] || !this.slides[index].element) return;
        
        // Mark as programmatic scroll to prevent stopping autoplay
        this.isProgrammaticScroll = true;
        
        // If triggered by autoplay, mark autoplay scrolling flag
        if (triggeredByAutoplay) {
            this.isAutoplayScrolling = true;
        }
        
        // If triggered by autoplay, preserve vertical scroll position
        let savedScrollY = null;
        if (triggeredByAutoplay) {
            savedScrollY = window.scrollY;
        }
        
        // Update pagination immediately for better UX
        this.setCurrentSlide(index);
        
        this.slides[index].element.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'start'
        });
        
        // Restore vertical position if this was autoplay
        if (triggeredByAutoplay && savedScrollY !== null) {
            // Use requestAnimationFrame to ensure it happens after scrollIntoView
            requestAnimationFrame(() => {
                window.scrollTo({
                    top: savedScrollY,
                    left: window.scrollX,
                    behavior: 'instant'
                });
            });
        }
        
        // Different reset times for different scenarios
        const resetDelay = triggeredByAutoplay ? 300 : 500;
        
        setTimeout(() => {
            this.isProgrammaticScroll = false;
            if (triggeredByAutoplay) {
                this.isAutoplayScrolling = false;
            }
        }, resetDelay); // Faster reset for autoplay, slower for manual to ensure smooth scroll completes
    }

    nextSlide() {
        if (!this.slides || this.slides.length === 0) return;
        
        console.log('[Carousel] nextSlide - current:', this.currentSlideIndex, 'total:', this.slides.length, 'autoplay:', this.isAutoPlaying, 'loop:', this.shouldLoop());
        
        // Mark that autoplay is triggering this scroll
        const wasTriggeredByAutoplay = this.isAutoPlaying;
        
        if (this.shouldLoop()) {
            const nextIndex = (this.currentSlideIndex + 1) % this.slides.length;
            console.log('[Carousel] nextSlide - going to index:', nextIndex);
            this.goToSlide(nextIndex, wasTriggeredByAutoplay);
        } else {
            // Non-looping: stop at last slide
            if (this.currentSlideIndex < this.slides.length - 1) {
                this.goToSlide(this.currentSlideIndex + 1, wasTriggeredByAutoplay);
            } else {
                // At last slide, stop autoplay
                console.log('[Carousel] nextSlide - at last slide, stopping autoplay');
                this.stopAutoplay();
            }
        }
    }

    previousSlide() {
        if (!this.slides || this.slides.length === 0) return;
        
        if (this.shouldLoop()) {
            const prevIndex = this.currentSlideIndex === 0 ? this.slides.length - 1 : this.currentSlideIndex - 1;
            this.goToSlide(prevIndex, false); // Manual navigation, not autoplay
        } else {
            // Non-looping: stop at first slide
            if (this.currentSlideIndex > 0) {
                this.goToSlide(this.currentSlideIndex - 1, false); // Manual navigation, not autoplay
            }
        }
    }

    startAutoplay() {
        if (this.isAutoPlaying) return;
        if (!this.slides || this.slides.length === 0) return;
        
        this.isAutoPlaying = true;
        const interval = this.getDatasetConfigValue(CarouselConfig.autoplayIntervalDatasetConfigKey) || CarouselConfig.defaultAutoplayInterval;
        
        this.autoplayInterval = setInterval(() => {
            this.nextSlide();
        }, interval);
        
        this.render();
    }

    stopAutoplay() {
        if (!this.isAutoPlaying) return;
        
        console.log('[Carousel] stopAutoplay called');
        this.isAutoPlaying = false;
        if (this.autoplayInterval) {
            clearInterval(this.autoplayInterval);
            this.autoplayInterval = null;
        }
        
        this.render();
    }

    toggleAutoplay() {
        if (this.isAutoPlaying) {
            this.stopAutoplay();
        } else {
            this.startAutoplay();
        }
    }

    shouldPlayOnLoad() {
        return this.getDatasetConfigValue(CarouselConfig.playOnLoadDatasetConfigKey) === true;
    }

    shouldLoop() {
        const loopConfig = this.getDatasetConfigValue(CarouselConfig.loopDatasetConfigKey);
        return loopConfig !== false && loopConfig !== 'false'; // Default to true
    }

    isZoomableByDefault() {
        const zoomableConfig = this.getDatasetConfigValue(CarouselConfig.zoomableDatasetConfigKey);
        return zoomableConfig !== undefined ? zoomableConfig : CarouselConfig.defaultZoomable;
    }

    randomizeSlides() {
        if (!this.viewport) {
            console.warn('[Carousel] Cannot randomize slides: no viewport found');
            return;
        }
        
        SlideRandomizer.randomizeSlides(this.viewport, CarouselConfig.slideSelector);
        console.log('[Carousel] Slides randomized');
    }

    addSlideNumbers() {
        if (!this.viewport) {
            console.warn('[Carousel] Cannot add slide numbers: no viewport found');
            return;
        }
        
        const slideElements = Array.from(this.viewport.querySelectorAll(CarouselConfig.slideSelector));
        SlideNumberer.addSlideNumbers(slideElements);
        console.log('[Carousel] Slide numbers added');
    }

    addPaginationNumbers() {
        if (!this.pagination || !this.pagination.dots) {
            console.warn('[Carousel] Cannot add pagination numbers: pagination not initialized');
            return;
        }
        
        const dotElements = this.pagination.dots.map(dot => dot.element);
        SlideNumberer.addPaginationNumbers(dotElements);
        
        // Add numbered modifier class to pagination
        this.pagination.element.classList.add('carousel__pagination--numbered');
        console.log('[Carousel] Pagination numbers added');
    }

    render() {
        this.conditionallySetHtmlClassState(this.isAutoPlaying, CarouselConfig.playingClassModifier);
    }

    // No destroy() override — teardowns are registered via onDestroy() in the constructor, and the
    // base Component#destroy() (called by the registry reaper) runs them, destroys children, and
    // deregisters this instance. See .claude/skills/js-components/references/js_component_base_class.md.
}